// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useGetPreferredPackageVersion } from '@hooks/queries/useGetPreferredPackageVersion';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardActionArea, CardMedia, Grid, Typography } from '@mui/material';
import imageCredentials from '../assets/credentials.webp';
import imageGradient from '../assets/gradient.png';
import imageRegistry from '../assets/registry.webp';
import { PackageName } from '../config/package';
import useStyles from './styles';

const Root: React.FC = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();

  const { data } = useGetPreferredPackageVersion(PackageName.CollateralApp);
  const collateralPackage = data?.packagePreference;
  const hasCollateralPackage = !!collateralPackage?.packageReference?.packageName;
  const collateralAppV2 = useGetPreferredPackageVersion(PackageName.CollateralAppV2);
  const collateralPackageV2 = collateralAppV2.data?.packagePreference;
  const hasCollateralPackageV2 = !!collateralPackageV2?.packageReference?.packageName;
  return (
    <Box sx={{ padding: 6 }}>
      <Grid container direction="column">
        <Grid item xs={12}>
          <Typography variant="h1" color="primary">
            Welcome to the DA Registry
          </Typography>
          <Typography variant="h5">The following modules are available to you:</Typography>
          <Grid container direction="row" spacing={4} sx={{ marginTop: 0 }}>
            <Grid item xs={12} sm={6} md={4} lg={4}>
              <Card className={classes.card}>
                <Box style={{ height: '100%' }}>
                  <CardActionArea onClick={() => navigate('/credential/onboarding')}>
                    <CardMedia
                      className={classes.cardMediaLarge}
                      image={imageCredentials}
                      title="Credentials"
                    />
                    <CardMedia
                      className={classes.cardMediaLargeOverlay}
                      image={imageGradient}
                      title="Credentials"
                    />
                    <Box className={classes.cardMediaText}>
                      <Typography className={classes.cardTitle}>Credentials</Typography>
                      <Typography className={classes.cardText}>Manage credentials</Typography>
                    </Box>
                  </CardActionArea>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={4}>
              <Card className={classes.card}>
                <Box style={{ height: '100%' }}>
                  <CardActionArea onClick={() => navigate('/registry/onboarding')}>
                    <CardMedia
                      className={classes.cardMediaLarge}
                      image={imageRegistry}
                      title="Registry"
                    />
                    <CardMedia
                      className={classes.cardMediaLargeOverlay}
                      image={imageGradient}
                      title="Registry"
                    />
                    <Box className={classes.cardMediaText}>
                      <Typography className={classes.cardTitle}>Registry</Typography>
                      <Typography className={classes.cardText}>Manage inventory</Typography>
                    </Box>
                  </CardActionArea>
                </Box>
              </Card>
            </Grid>
            {hasCollateralPackage && (
              <Grid item xs={12} sm={6} md={4} lg={4}>
                <Card className={classes.card}>
                  <Box style={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate('/collateral/onboarding')}>
                      <CardMedia
                        className={classes.cardMediaLarge}
                        image={imageRegistry}
                        title="Collateral"
                      />
                      <CardMedia
                        className={classes.cardMediaLargeOverlay}
                        image={imageGradient}
                        title="Collateral"
                      />
                      <Box className={classes.cardMediaText}>
                        <Typography className={classes.cardTitle}>Collateral</Typography>
                        <Typography className={classes.cardText}>Manage Collateral</Typography>
                      </Box>
                    </CardActionArea>
                  </Box>
                </Card>
              </Grid>
            )}
            {hasCollateralPackageV2 && (
              <Grid item xs={12} sm={6} md={4} lg={4}>
                <Card className={classes.card}>
                  <Box style={{ height: '100%' }}>
                    <CardActionArea onClick={() => navigate('/collateralV2/onboarding')}>
                      <CardMedia
                        className={classes.cardMediaLarge}
                        image={imageRegistry}
                        title="Collateral V2"
                      />
                      <CardMedia
                        className={classes.cardMediaLargeOverlay}
                        image={imageGradient}
                        title="Collateral V2"
                      />
                      <Box className={classes.cardMediaText}>
                        <Typography className={classes.cardTitle}>Collateral V2</Typography>
                        <Typography className={classes.cardText}>Manage Collateral</Typography>
                      </Box>
                    </CardActionArea>
                  </Box>
                </Card>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Root;
